import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";
import {
  categorySchema,
  parseApiResponse,
  transactionSchema,
  transactionTypeSchema,
} from "@/api/responseSchemas";
import type { Category } from "@/hooks/useCategories";
import type { Transaction, TransactionType } from "@/hooks/useTransactions";

export interface ProjectionCategory {
  category_id: number | null;
  category_name: string;
  current_amount: number;
  projected_amount: number;
  historical_average: number;
  variance_from_average: number;
}

export interface ProjectionResponse {
  period_month: string;
  baseline_periods: string[];
  expected_income: number;
  expected_expenses: number;
  expected_balance: number;
  confidence_score: number;
  current_cycle_income: number;
  current_cycle_expenses: number;
  category_projections: ProjectionCategory[];
}

export type ProjectionQueryParams = {
  projection_date?: string;
  baseline_cycles?: number;
};

const projectionCategorySchema = z.looseObject({
  category_id: z.number().nullable(),
  category_name: z.string(),
  current_amount: z.number(),
  projected_amount: z.number(),
  historical_average: z.number(),
  variance_from_average: z.number(),
});

const projectionResponseSchema: z.ZodType<ProjectionResponse> = z.looseObject({
  period_month: z.string(),
  baseline_periods: z.array(z.string()).default([]),
  expected_income: z.number(),
  expected_expenses: z.number(),
  expected_balance: z.number(),
  confidence_score: z.number(),
  current_cycle_income: z.number(),
  current_cycle_expenses: z.number(),
  category_projections: z.array(projectionCategorySchema).default([]),
});

const toMonthStart = (dateValue: string) => {
  const date = new Date(`${dateValue}T00:00:00`);
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const toYMD = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addMonths = (date: Date, months: number) =>
  new Date(date.getFullYear(), date.getMonth() + months, 1);

const assertProjectionResponse = (data: unknown): ProjectionResponse => {
  if (data && typeof data === "object" && "error" in data) {
    const error = (data as { error?: unknown }).error;
    throw new Error(typeof error === "string" ? error : "Projection endpoint returned an error.");
  }

  return parseApiResponse(
    projectionResponseSchema,
    data,
    "GET /transactions/projection",
  );
};

const sumTransactions = (
  transactions: Transaction[],
  typeIds: Set<number>,
) =>
  transactions.reduce(
    (total, transaction) =>
      typeIds.has(transaction.type_id) ? total + Number(transaction.amount ?? 0) : total,
    0,
  );

const buildFallbackProjection = async (params?: ProjectionQueryParams): Promise<ProjectionResponse> => {
  const baselineCycles = Math.max(1, Number(params?.baseline_cycles ?? 3));
  const periodMonth = toMonthStart(params?.projection_date ?? toYMD(new Date()));
  const startPeriod = addMonths(periodMonth, -baselineCycles);
  const endPeriod = addMonths(periodMonth, 1);
  endPeriod.setDate(0);

  const [transactionsResponse, transactionTypesResponse, categoriesResponse] =
    await Promise.all([
      apiClient.get("/transactions/", {
        params: {
          billable_date_gte: toYMD(startPeriod),
          billable_date_lte: toYMD(endPeriod),
          transaction_date: "asc",
        },
      }),
      apiClient.get("/transactions/types"),
      apiClient.get("/categories/"),
    ]);
  const transactions: Transaction[] = parseApiResponse(
    transactionSchema.array(),
    transactionsResponse.data,
    "GET /transactions (projection fallback)",
  );
  const transactionTypes: TransactionType[] = parseApiResponse(
    transactionTypeSchema.array(),
    transactionTypesResponse.data,
    "GET /transactions/types (projection fallback)",
  );
  const categories: Category[] = parseApiResponse(
    categorySchema.array(),
    categoriesResponse.data,
    "GET /categories (projection fallback)",
  );

  const depositTypeIds = new Set(
    transactionTypes.filter((type) => type.name === "DEPOSIT").map((type) => type.id),
  );
  const expenseTypeIds = new Set(
    transactionTypes
      .filter((type) => type.name === "EXPENSE" || type.name === "TRANSFER")
      .map((type) => type.id),
  );
  const categoryNameById = new Map(categories.map((category) => [category.id, category.name]));
  const baselinePeriods = Array.from({ length: baselineCycles }, (_, index) =>
    toYMD(addMonths(periodMonth, index - baselineCycles)),
  );

  const isSameMonth = (transaction: Transaction, month: Date) => {
    const transactionDate = new Date(`${transaction.transaction_date.slice(0, 10)}T00:00:00`);
    return transactionDate.getFullYear() === month.getFullYear() && transactionDate.getMonth() === month.getMonth();
  };

  const baselineTransactions = transactions.filter((transaction) =>
    baselinePeriods.some((period) => isSameMonth(transaction, toMonthStart(period))),
  );
  const currentTransactions = transactions.filter((transaction) => isSameMonth(transaction, periodMonth));
  const expectedIncome = Math.max(
    sumTransactions(currentTransactions, depositTypeIds),
    sumTransactions(baselineTransactions, depositTypeIds) / baselineCycles,
  );
  const currentCycleExpenses = sumTransactions(currentTransactions, expenseTypeIds);
  const currentCycleIncome = sumTransactions(currentTransactions, depositTypeIds);

  const historicalExpenseByCategory = new Map<number | null, number>();
  const currentExpenseByCategory = new Map<number | null, number>();

  baselineTransactions.forEach((transaction) => {
    if (!expenseTypeIds.has(transaction.type_id)) return;
    const categoryId = transaction.category_id ?? null;
    historicalExpenseByCategory.set(
      categoryId,
      (historicalExpenseByCategory.get(categoryId) ?? 0) + Number(transaction.amount ?? 0),
    );
  });
  currentTransactions.forEach((transaction) => {
    if (!expenseTypeIds.has(transaction.type_id)) return;
    const categoryId = transaction.category_id ?? null;
    currentExpenseByCategory.set(
      categoryId,
      (currentExpenseByCategory.get(categoryId) ?? 0) + Number(transaction.amount ?? 0),
    );
  });

  const categoryIds = new Set([...historicalExpenseByCategory.keys(), ...currentExpenseByCategory.keys()]);
  const categoryProjections = [...categoryIds].map((categoryId) => {
    const historicalAverage = (historicalExpenseByCategory.get(categoryId) ?? 0) / baselineCycles;
    const currentAmount = currentExpenseByCategory.get(categoryId) ?? 0;
    const projectedAmount = Math.max(currentAmount, historicalAverage);

    return {
      category_id: categoryId,
      category_name: categoryId == null ? "Uncategorized" : (categoryNameById.get(categoryId) ?? `Category ${categoryId}`),
      current_amount: Number(currentAmount.toFixed(2)),
      projected_amount: Number(projectedAmount.toFixed(2)),
      historical_average: Number(historicalAverage.toFixed(2)),
      variance_from_average: Number((currentAmount - historicalAverage).toFixed(2)),
    };
  });

  const expectedExpenses = categoryProjections.reduce(
    (total, category) => total + category.projected_amount,
    0,
  );

  return {
    period_month: toYMD(periodMonth),
    baseline_periods: baselinePeriods,
    expected_income: Number(expectedIncome.toFixed(2)),
    expected_expenses: Number(expectedExpenses.toFixed(2)),
    expected_balance: Number((expectedIncome - expectedExpenses).toFixed(2)),
    confidence_score: Math.min(85, baselineCycles * 25),
    current_cycle_income: Number(currentCycleIncome.toFixed(2)),
    current_cycle_expenses: Number(currentCycleExpenses.toFixed(2)),
    category_projections: categoryProjections,
  };
};

export const useTransactionProjection = (params?: ProjectionQueryParams) => {
  return useQuery<ProjectionResponse, unknown>({
    queryKey: ["analytics", "transactionProjection", params ?? {}],
    queryFn: async () => {
      try {
        const res = await apiClient.get("/transactions/projection", {
          params,
        });
        return assertProjectionResponse(res.data);
      } catch {
        return buildFallbackProjection(params);
      }
    },
  });
};
